import { push } from 'connected-react-router';
import { getAuctionEditUrl } from 'utils/auction';
import {
    Campaign,
    convertCampaignSourcesFromDto,
    convertRegionsFromDto,
    getAdCreateCampaignBatchActions,
    getAdEditOrCopyCampaignBatchActions,
    getBannerCreateBatchActions,
    getBannerEditBatchActions,
    getCampaignIdFromMatch,
} from 'utils/campaign';
import { SourceConfigDto, VacancyInfoRequirement } from 'utils/campaign/requirementUtils/types';
import { CampaignProductType } from 'utils/constants/campaign';
import { BEProfRoles, hasProfRoles } from 'utils/profRoles';

import { ModelData } from '@hh.ru/magritte-ui';

import { Match, Requirements } from 'app/routes';

import store from 'store';
import { PROF_ROLES, RegionBE } from 'store/autoReducers';
import { selectMainMenuLink, selectMainMenuVacancyId } from 'store/models/mainMenu';

const isCampaignEdit = (match: Match['match']) => Boolean(getCampaignIdFromMatch(match));

export const CpcFlexibleBuilderPageRequirements: Requirements<{
    campaign: Campaign;
    regions: RegionBE[];
    existRequisites: { existRequisites: boolean };
    vacancyInfo: VacancyInfoRequirement;
    sourcesConfig: SourceConfigDto[];
    [PROF_ROLES]?: BEProfRoles;
    erirClassificationCatalog: ModelData[];
}> = {
    methods: ({ state, match }) => {
        const campaignId = getCampaignIdFromMatch(match) || '';
        const vacancyId = selectMainMenuVacancyId(state);

        return {
            regions: { url: '/api/v1/catalog/areas' },
            existRequisites: { url: '/api/v1/requisites/exist_requisites', cacheable: false },
            ...(vacancyId && {
                vacancyInfo: { url: `/api/v1/vacancy/campaign_info_from_vacancy?vacancyId=${vacancyId}` },
            }),
            ...(isCampaignEdit(match) && {
                campaign: { url: `/api/v1/campaigns/${campaignId}`, cacheable: false },
            }),
            erirClassificationCatalog: { url: '/api/v1/catalog/erir/classification/tree' },
            [PROF_ROLES]: {
                url: '/api/v1/catalog/professionalCatalog',
            },
        };
    },
    isDataReady: ({ result, match }) => (isCampaignEdit(match) ? hasProfRoles(result) : true),
    resolve: ({
        result: {
            regions,
            vacancyInfo,
            campaign,
            existRequisites: { existRequisites },
            erirClassificationCatalog,
            ...professions
        },
        match,
        state,
    }) => {
        if (isCampaignEdit(match) && campaign.productType === CampaignProductType.SearchAuction && campaign.vacancyId) {
            // если по ошибке открыли аукцион, не даем редактировать, редиректим на правильную форму
            const auctionUrl = getAuctionEditUrl(campaign.vacancyId);
            store.dispatch(push(auctionUrl));
            return [];
        }

        return [
            ...getAdCreateCampaignBatchActions({
                regions: convertRegionsFromDto(regions),
                mainMenuFormData: { vacancyId: selectMainMenuVacancyId(state), link: selectMainMenuLink(state) },
                vacancyInfo,
                existRequisites,
                erirClassificationCatalog,
                ...professions,
            }),
            ...(isCampaignEdit(match)
                ? [
                      ...getAdEditOrCopyCampaignBatchActions({
                          campaign: {
                              ...campaign,
                              apiSources: convertCampaignSourcesFromDto(campaign.campaignSources),
                          },
                          step: state.router.location.query?.step,
                      }),
                      ...getBannerCreateBatchActions({
                          campaign: {
                              ...campaign,
                              apiSources: convertCampaignSourcesFromDto(campaign.campaignSources),
                          },
                          campaignIdForBanner: String(getCampaignIdFromMatch(match)),
                      }),
                      ...getBannerEditBatchActions({
                          banner: campaign.banner,
                      }),
                  ]
                : []),
        ];
    },
};
